import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { GarageVehicle, LimitType, MaintenanceRecord, FuelRecord, Vehicle, FavoriteVehicle } from '../types/types';
import { FREE_LIMITS } from '../constants/limits';
import { supabase } from '../lib/supabase';
import { getSubscriptionStatus, hasActiveSubscription } from '../lib/stripe';
import { getLogger } from '../utils/logger';

// Create a namespaced logger
const logger = getLogger('AppContext');


// Define the state type
interface AppState {
  freeAssessmentsUsed: number;
  freeCarLensResultsUsed: number;
  freeAskCarsjudgeQuestionsUsed: number;
  garageVehicles: GarageVehicle[];
  favoriteVehicles: FavoriteVehicle[];
  subscriptionStatus: { status: string } | null;
  showLimitModal: boolean;
  limitType: LimitType | null;
  selectedVehicle: any | null; // Add selectedVehicle to state
  isLoading: boolean;
  searchHistory: any[];
}

// Define the action types
type AppAction = 
  | { type: 'SET_SUBSCRIPTION_STATUS'; payload: { status: string } | null }
  | { type: 'SET_SHOW_LIMIT_MODAL'; payload: { show: boolean; type: LimitType | null } }
  | { type: 'SET_GARAGE_VEHICLES'; payload: GarageVehicle[] }
  | { type: 'SET_FAVORITE_VEHICLES'; payload: FavoriteVehicle[] }
  | { type: 'ADD_GARAGE_VEHICLE'; payload: GarageVehicle }
  | { type: 'ADD_FAVORITE_VEHICLE'; payload: FavoriteVehicle }
  | { type: 'REMOVE_FAVORITE_VEHICLE'; payload: string }
  | { type: 'UPDATE_GARAGE_VEHICLE'; payload: GarageVehicle }
  | { type: 'REMOVE_GARAGE_VEHICLE'; payload: string }
  | { type: 'SET_USAGE_COUNTS'; payload: { assessments: number; carLens: number; askCarsjudge: number } }
  | { type: 'INCREMENT_ASSESSMENT_USED' }
  | { type: 'INCREMENT_CAR_LENS_USED' }
  | { type: 'INCREMENT_ASK_CARSJUDGE_USED' }
  | { type: 'SET_SELECTED_VEHICLE'; payload: any | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_USAGE_COUNTS' };

// Create the context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  checkAndHandleLimits: (type: LimitType) => Promise<boolean>;
  assessVehicle: (vehicle: any | null) => void;
  incrementAssessmentUsed: () => void;
  incrementCarLensUsed: () => void;
  incrementAskCarsjudgeUsed: () => void;
  setShowLimitModal: (show: boolean, type: LimitType | null) => void;
  resetUsageCounts: () => void;
  addToGarage: (vehicle: Vehicle, mileage: number, purchaseDate: string) => Promise<GarageVehicle | null>;
  updateGarageVehicle: (updatedVehicle: GarageVehicle) => Promise<void>;
  removeFromGarage: (vehicleId: string) => Promise<void>;
  addMaintenanceRecord: (vehicleId: string, record: MaintenanceRecord) => Promise<void>;
  addFuelRecord: (vehicleId: string, record: FuelRecord) => Promise<void>;
  addToFavorites: (vehicle: Vehicle) => Promise<FavoriteVehicle | null>;
  removeFromFavorites: (favoriteId: string) => Promise<void>;
  isVehicleFavorited: (vehicleId: string) => boolean;
} | undefined>(undefined);

// Initial state
const initialState: AppState = {
  freeAssessmentsUsed: 0,
  freeCarLensResultsUsed: 0,
  freeAskCarsjudgeQuestionsUsed: 0,
  garageVehicles: [],
  favoriteVehicles: [],
  subscriptionStatus: null,
  showLimitModal: false,
  limitType: null,
  selectedVehicle: null,
  isLoading: false,
  searchHistory: []
};

// Reducer function
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_SUBSCRIPTION_STATUS':
      return {
        ...state,
        subscriptionStatus: action.payload
      };
    case 'SET_SHOW_LIMIT_MODAL':
      return {
        ...state,
        showLimitModal: action.payload.show,
        limitType: action.payload.type
      };
    case 'SET_GARAGE_VEHICLES':
      return {
        ...state,
        garageVehicles: action.payload
      };
    case 'SET_FAVORITE_VEHICLES':
      return {
        ...state,
        favoriteVehicles: action.payload
      };
    case 'ADD_GARAGE_VEHICLE':
      return {
        ...state,
        garageVehicles: [...state.garageVehicles, action.payload]
      };
    case 'ADD_FAVORITE_VEHICLE':
      return {
        ...state,
        favoriteVehicles: [...state.favoriteVehicles, action.payload]
      };
    case 'REMOVE_FAVORITE_VEHICLE':
      return {
        ...state,
        favoriteVehicles: state.favoriteVehicles.filter(v => v.id !== action.payload)
      };
    case 'UPDATE_GARAGE_VEHICLE':
      return {
        ...state,
        garageVehicles: state.garageVehicles.map(v =>
          v.id === action.payload.id ? action.payload : v
        )
      };
    case 'REMOVE_GARAGE_VEHICLE':
      return {
        ...state,
        garageVehicles: state.garageVehicles.filter(v => v.id !== action.payload)
      };
    case 'SET_USAGE_COUNTS':
      return {
        ...state,
        freeAssessmentsUsed: action.payload.assessments,
        freeCarLensResultsUsed: action.payload.carLens,
        freeAskCarsjudgeQuestionsUsed: action.payload.askCarsjudge
      };
    case 'INCREMENT_ASSESSMENT_USED':
      return {
        ...state,
        freeAssessmentsUsed: state.freeAssessmentsUsed + 1
      };
    case 'INCREMENT_CAR_LENS_USED':
      return {
        ...state,
        freeCarLensResultsUsed: state.freeCarLensResultsUsed + 1
      };
    case 'INCREMENT_ASK_CARSJUDGE_USED':
      return {
        ...state,
        freeAskCarsjudgeQuestionsUsed: state.freeAskCarsjudgeQuestionsUsed + 1
      };
    case 'SET_SELECTED_VEHICLE':
      return {
        ...state,
        selectedVehicle: action.payload
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    case 'RESET_USAGE_COUNTS':
      return {
        ...state,
        freeAssessmentsUsed: 0,
        freeCarLensResultsUsed: 0,
        freeAskCarsjudgeQuestionsUsed: 0
      };
    default:
      return state;
  }
}

// Provider component
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Fetch initial data (garage vehicles, subscription status, and usage counts)
  useEffect(() => {
    const fetchData = async () => {
      logger.log('Starting fetchData...');
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        logger.log('Checking for current user...');
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        // Handle authentication errors
        if (userError) {
          // Check for session-related errors that are expected and should be logged as info, not errors
          if (userError.message?.includes('session_not_found') || 
              userError.message?.includes('JWT expired') ||
              userError.message?.includes('invalid_token') ||
              userError.message?.includes('Auth session missing!')) {
            logger.log('No valid session found (expected when not logged in):', userError.message);
            await supabase.auth.signOut();
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
          } else {
            // Log other authentication errors as actual errors
            logger.error('Authentication error:', userError);
          }
        }
        
        // If no user is found, just set loading to false and return
        if (!user) {
          logger.log('No authenticated user found');
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        
        logger.log('User found:', user.id);
        
        // Fetch subscription status
        logger.log('Fetching subscription status...');
        const subscription = await getSubscriptionStatus();
        logger.log('Subscription status:', subscription?.status || 'none');
        dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: subscription });

        // Fetch garage vehicles
        logger.log('Fetching garage vehicles...');
        // Records come from the masking VIEWS, not the base tables. Since the
        // fleet migration, a driver reading maintenance_records directly gets
        // only rows they authored themselves — so on the base tables a fleet
        // driver would see a nearly empty service history instead of a full
        // one with the money hidden.
        //
        // The user_id filter is gone for the same reason: fleet vehicles carry
        // the manager's user_id, so filtering by the signed-in user hid every
        // company vehicle from its own drivers. RLS already returns exactly
        // what this person may see — personal cars plus assigned fleet ones —
        // so re-filtering here could only ever subtract from that.
        const { data: vehiclesData, error: vehiclesError } = await supabase
          .from('vehicles')
          .select(`
            *,
            v_maintenance_records (*),
            v_fuel_records (*)
          `);

        if (vehiclesError) {
          logger.error('Error fetching vehicles:', vehiclesError);
          throw vehiclesError;
        }

        logger.log('Vehicles fetched:', vehiclesData?.length || 0);
        const garageVehicles: GarageVehicle[] = vehiclesData.map((v: any) => ({
          id: v.id,
          year: v.year,
          make: v.make,
          model: v.model,
          trim: v.trim || '',
          // The column is `current_mileage`; there is no `mileage` on vehicles.
          // This mapped to undefined, and GaragePage calls
          // `vehicle.mileage.toLocaleString()` on it — so the garage threw
          // "Cannot read properties of undefined" and rendered nothing as soon
          // as an account actually had a vehicle in it.
          mileage: v.current_mileage ?? 0,
          purchaseDate: v.purchase_date,
          addedToGarageDate: v.created_at,
          image_url: v.image_url,
          reliability: {
            score: 7.5, // Default value
            commonIssues: [],
            averageMaintenanceCost: 500, // Default value
            expectedLifespan: 12 // Default value
          },
          assessment: {
            condition: 'good', // Default value
            keyFindings: [],
            recommendations: [],
            urgentIssues: []
          },
          pros: [],
          cons: [],
          // Mapped from the view columns: the table stores service_type /
          // service_date / notes, while the UI type calls them type / date /
          // description. `cost` arrives null when a fleet driver is looking at
          // someone else's entry, and is left null rather than coerced to 0 so
          // a masked amount is never rendered as "free".
          maintenanceRecords: (v.v_maintenance_records || []).map((r: any) => ({
            id: r.id,
            date: r.service_date,
            type: r.service_type,
            description: r.notes ?? '',
            mileage: r.mileage,
            cost: r.cost,
            costsVisible: r.costs_visible !== false,
          })),
          maintenanceSchedule: [],
          notifications: [],
          fuelRecords: (v.v_fuel_records || []).map((r: any) => ({
            id: r.id,
            date: r.date,
            cost: r.total_cost,
            mileage: r.mileage,
            costsVisible: r.costs_visible !== false,
          }))
        }));
        
        dispatch({ type: 'SET_GARAGE_VEHICLES', payload: garageVehicles });

        // Fetch favorite vehicles
        logger.log('Fetching favorite vehicles...');
        const { data: favoritesData, error: favoritesError } = await supabase
          .from('favorite_vehicles')
          .select('*')
          .eq('user_id', user.id);

        if (favoritesError) {
          logger.error('Error fetching favorites:', favoritesError);
          throw favoritesError;
        }

        if (favoritesData) {
          logger.log('Favorites fetched:', favoritesData.length);
          const favoriteVehicles: FavoriteVehicle[] = favoritesData.map((fav: any) => ({
            id: fav.id,
            vehicleId: fav.vehicle_id,
            userId: fav.user_id,
            vehicle: JSON.parse(fav.vehicle_data),
            addedAt: fav.created_at
          }));
          
          dispatch({ type: 'SET_FAVORITE_VEHICLES', payload: favoriteVehicles });
        }

        // Fetch usage counts from user_usage_limits table (matches mobile app)
        logger.log('Fetching user usage data...');
        const { data: usageData, error: usageError } = await supabase
          .from('user_usage_limits')
          .select('assessment_count, chat_count, car_lens_count')
          .eq('user_id', user.id)
          .maybeSingle();

        if (usageError) {
          logger.error('Error fetching user usage data:', usageError);
          dispatch({
            type: 'SET_USAGE_COUNTS',
            payload: { assessments: 0, carLens: 0, askCarsjudge: 0 }
          });
        } else if (usageData) {
          logger.log('User usage data fetched:', usageData);
          dispatch({
            type: 'SET_USAGE_COUNTS',
            payload: {
              assessments: usageData.assessment_count || 0,
              carLens: usageData.car_lens_count || 0,
              askCarsjudge: usageData.chat_count || 0
            }
          });
        } else {
          logger.log('Usage record not found, creating with defaults');
          try {
            await supabase
              .from('user_usage_limits')
              .upsert({
                user_id: user.id,
                assessment_count: 0,
                chat_count: 0,
                car_lens_count: 0,
                document_count: 0,
                garage_count: 0,
                receipt_upload_count: 0
              });
            dispatch({
              type: 'SET_USAGE_COUNTS',
              payload: { assessments: 0, carLens: 0, askCarsjudge: 0 }
            });
          } catch (insertError) {
            logger.error('Error creating usage record:', insertError);
            dispatch({
              type: 'SET_USAGE_COUNTS',
              payload: { assessments: 0, carLens: 0, askCarsjudge: 0 }
            });
          }
        }
        
        logger.log('fetchData completed successfully');
      } catch (error: any) {
        logger.error('Error fetching initial app data:', error);
        
        // Handle authentication-related errors
        if (error?.message?.includes('session_not_found') || 
            error?.message?.includes('JWT expired') ||
            error?.message?.includes('invalid_token') ||
            error?.message?.includes('Auth session missing!')) {
          logger.log('Authentication error detected, signing out...');
          await supabase.auth.signOut();
        }
      } finally {
        logger.log('Setting loading state to false');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    logger.log('Initial useEffect running, calling fetchData...');
    fetchData();

    // Listen for auth changes to re-fetch data
    logger.log('Setting up auth state change listener...');
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      logger.log('Auth state changed. Event:', event);
      logger.log('Session exists:', !!session);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        logger.log('User signed in or token refreshed, fetching data...');
        fetchData();
      } else if (event === 'SIGNED_OUT') {
        logger.log('User signed out, resetting state...');
        // Reset state when user signs out
        dispatch({ type: 'SET_GARAGE_VEHICLES', payload: [] });
        dispatch({ type: 'SET_FAVORITE_VEHICLES', payload: [] });
        dispatch({ 
          type: 'SET_USAGE_COUNTS', 
          payload: {
            assessments: 0,
            carLens: 0,
            askCarsjudge: 0
          }
        });
        dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: null });
      }
    });

    return () => {
      logger.log('Cleaning up auth listener...');
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkAndHandleLimits = useCallback(async (type: LimitType): Promise<boolean> => {
    logger.log('Checking limits for:', type);

    try {
      // Fetch fresh subscription from profiles table
      const freshSubscription = await getSubscriptionStatus();
      logger.log('Fresh subscription status:', freshSubscription?.status);

      if (freshSubscription?.status !== state.subscriptionStatus?.status) {
        dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: freshSubscription });
      }

      // Use same logic as mobile app for premium check
      if (hasActiveSubscription(freshSubscription)) {
        logger.log('User has active subscription, bypassing limits');
        return true;
      }

      let isLimitReached = false;
      switch (type) {
        case 'assessment':
          isLimitReached = state.freeAssessmentsUsed >= FREE_LIMITS.ASSESSMENTS;
          break;
        case 'carlens':
          isLimitReached = state.freeCarLensResultsUsed >= FREE_LIMITS.CAR_LENS;
          break;
        case 'askcarsjudge':
          isLimitReached = state.freeAskCarsjudgeQuestionsUsed >= FREE_LIMITS.ASK_CARSJUDGE;
          break;
        case 'garage':
          isLimitReached = state.garageVehicles.length >= FREE_LIMITS.GARAGE_CARS;
          break;
      }

      if (isLimitReached) {
        logger.log('Limit reached for:', type);
        dispatch({ type: 'SET_SHOW_LIMIT_MODAL', payload: { show: true, type } });
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error checking limits:', error);
      // On error, enforce limits as safety measure
      let isLimitReached = false;
      switch (type) {
        case 'assessment':
          isLimitReached = state.freeAssessmentsUsed >= FREE_LIMITS.ASSESSMENTS;
          break;
        case 'carlens':
          isLimitReached = state.freeCarLensResultsUsed >= FREE_LIMITS.CAR_LENS;
          break;
        case 'askcarsjudge':
          isLimitReached = state.freeAskCarsjudgeQuestionsUsed >= FREE_LIMITS.ASK_CARSJUDGE;
          break;
        case 'garage':
          isLimitReached = state.garageVehicles.length >= FREE_LIMITS.GARAGE_CARS;
          break;
      }

      if (isLimitReached) {
        dispatch({ type: 'SET_SHOW_LIMIT_MODAL', payload: { show: true, type } });
        return false;
      }
      return true;
    }
  }, [state, dispatch]);

  // Add the missing functions
  const assessVehicle = useCallback((vehicle: any | null) => {
    logger.log('Setting selected vehicle:', vehicle?.id || 'null');
    dispatch({ type: 'SET_SELECTED_VEHICLE', payload: vehicle });
  }, [dispatch]);

  const incrementAssessmentUsed = useCallback(async () => {
    logger.log('Incrementing assessment usage count');
    dispatch({ type: 'INCREMENT_ASSESSMENT_USED' });
    
    try {
      logger.log('Getting current user for updating assessment count...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        logger.error('Error getting user for assessment count update:', userError);
        return;
      }
      
      if (!user) {
        logger.log('No user found for assessment count update');
        return;
      }

      logger.log('Updating assessment count in database for user:', user.id);
      const { error: updateError } = await supabase
        .from('user_usage_limits')
        .update({ assessment_count: state.freeAssessmentsUsed + 1 })
        .eq('user_id', user.id);
        
      if (updateError) {
        logger.error('Error updating assessment count:', updateError);
      } else {
        logger.log('Assessment count updated successfully');
      }
    } catch (error) {
      logger.error('Error updating assessment usage count:', error);
    }
  }, [dispatch, state.freeAssessmentsUsed]);

  const incrementCarLensUsed = useCallback(async () => {
    logger.log('Incrementing Car Lens usage count');
    dispatch({ type: 'INCREMENT_CAR_LENS_USED' });
    
    try {
      logger.log('Getting current user for updating Car Lens count...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        logger.error('Error getting user for Car Lens count update:', userError);
        return;
      }
      
      if (!user) {
        logger.log('No user found for Car Lens count update');
        return;
      }

      logger.log('Updating Car Lens count in database for user:', user.id);
      const { error: updateError } = await supabase
        .from('user_usage_limits')
        .update({ car_lens_count: state.freeCarLensResultsUsed + 1 })
        .eq('user_id', user.id);
        
      if (updateError) {
        logger.error('Error updating Car Lens count:', updateError);
      } else {
        logger.log('Car Lens count updated successfully');
      }
    } catch (error) {
      logger.error('Error updating Car Lens usage count:', error);
    }
  }, [dispatch, state.freeCarLensResultsUsed]);

  const incrementAskCarsjudgeUsed = useCallback(async () => {
    logger.log('Incrementing Ask Carsjudge usage count');
    dispatch({ type: 'INCREMENT_ASK_CARSJUDGE_USED' });
    
    try {
      logger.log('Getting current user for updating Ask Carsjudge count...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        logger.error('Error getting user for Ask Carsjudge count update:', userError);
        return;
      }
      
      if (!user) {
        logger.log('No user found for Ask Carsjudge count update');
        return;
      }

      logger.log('Updating Ask Carsjudge count in database for user:', user.id);
      const { error: updateError } = await supabase
        .from('user_usage_limits')
        .update({ chat_count: state.freeAskCarsjudgeQuestionsUsed + 1 })
        .eq('user_id', user.id);
        
      if (updateError) {
        logger.error('Error updating Ask Carsjudge count:', updateError);
      } else {
        logger.log('Ask Carsjudge count updated successfully');
      }
    } catch (error) {
      logger.error('Error updating Ask Carsjudge usage count:', error);
    }
  }, [dispatch, state.freeAskCarsjudgeQuestionsUsed]);

  const setShowLimitModal = useCallback((show: boolean, type: LimitType | null) => {
    logger.log('Setting limit modal visibility:', show, 'for type:', type);
    dispatch({ type: 'SET_SHOW_LIMIT_MODAL', payload: { show, type } });
  }, [dispatch]);

  const resetUsageCounts = useCallback(async () => {
    logger.log('Resetting usage counts');
    dispatch({ type: 'RESET_USAGE_COUNTS' });
    
    try {
      logger.log('Getting current user for resetting usage counts...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        logger.error('Error getting user for usage count reset:', userError);
        return;
      }
      
      if (!user) {
        logger.log('No user found for usage count reset');
        return;
      }

      logger.log('Resetting usage counts in database for user:', user.id);
      const { error: updateError } = await supabase
        .from('user_usage_limits')
        .update({
          assessment_count: 0,
          chat_count: 0,
          car_lens_count: 0,
          document_count: 0,
          garage_count: 0,
          receipt_upload_count: 0
        })
        .eq('user_id', user.id);
        
      if (updateError) {
        logger.error('Error resetting usage counts:', updateError);
      } else {
        logger.log('Usage counts reset successfully');
      }
    } catch (error) {
      logger.error('Error resetting usage counts:', error);
    }
  }, [dispatch]);

  const addToGarage = useCallback(async (vehicle: Vehicle, mileage: number, purchaseDate: string): Promise<GarageVehicle | null> => {
    logger.log('Adding vehicle to garage:', {
      vehicle: vehicle.id,
      mileage,
      purchaseDate
    });
    
    try {
      logger.log('Getting current user for garage addition...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        logger.error('Error getting user for garage addition:', userError);
        throw new Error('User not authenticated');
      }
      
      if (!user) {
        logger.log('No user found for garage addition');
        throw new Error('User not authenticated');
      }

      // Check if we can add to garage (limit check)
      const canAdd = await checkAndHandleLimits('garage');
      if (!canAdd) {
        return null;
      }

      logger.log('Inserting vehicle into database...');
      const { data: insertedVehicle, error: insertError } = await supabase
        .from('vehicles')
        .insert({
          user_id: user.id,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.trim || '',
          mileage: mileage,
          purchase_date: purchaseDate,
          image_url: vehicle.image_url
        })
        .select()
        .single();

      if (insertError) {
        logger.error('Error inserting vehicle:', insertError);
        throw insertError;
      }

      logger.log('Vehicle inserted successfully:', insertedVehicle.id);

      const garageVehicle: GarageVehicle = {
        id: insertedVehicle.id,
        year: insertedVehicle.year,
        make: insertedVehicle.make,
        model: insertedVehicle.model,
        trim: insertedVehicle.trim || '',
        mileage: insertedVehicle.mileage,
        purchaseDate: insertedVehicle.purchase_date,
        addedToGarageDate: insertedVehicle.created_at,
        image_url: insertedVehicle.image_url,
        reliability: {
          score: 7.5,
          commonIssues: [],
          averageMaintenanceCost: 500,
          expectedLifespan: 12
        },
        assessment: {
          condition: 'good',
          keyFindings: [],
          recommendations: [],
          urgentIssues: []
        },
        pros: [],
        cons: [],
        maintenanceRecords: [],
        maintenanceSchedule: [],
        notifications: [],
        fuelRecords: []
      };

      dispatch({ type: 'ADD_GARAGE_VEHICLE', payload: garageVehicle });
      logger.log('Vehicle added to garage successfully');
      return garageVehicle;
    } catch (error) {
      logger.error('Error adding vehicle to garage:', error);
      return null;
    }
  }, [state.garageVehicles.length, checkAndHandleLimits, dispatch]);

  const updateGarageVehicle = useCallback(async (updatedVehicle: GarageVehicle): Promise<void> => {
    logger.log('Updating garage vehicle:', updatedVehicle.id);
    
    try {
      const { error } = await supabase
        .from('vehicles')
        .update({
          year: updatedVehicle.year,
          make: updatedVehicle.make,
          model: updatedVehicle.model,
          trim: updatedVehicle.trim,
          mileage: updatedVehicle.mileage,
          purchase_date: updatedVehicle.purchaseDate,
          image_url: updatedVehicle.image_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedVehicle.id);

      if (error) {
        logger.error('Error updating vehicle:', error);
        throw error;
      }

      dispatch({ type: 'UPDATE_GARAGE_VEHICLE', payload: updatedVehicle });
      logger.log('Vehicle updated successfully');
    } catch (error) {
      logger.error('Error updating garage vehicle:', error);
      throw error;
    }
  }, [dispatch]);

  const removeFromGarage = useCallback(async (vehicleId: string): Promise<void> => {
    logger.log('Removing vehicle from garage:', vehicleId);
    
    try {
      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', vehicleId);

      if (error) {
        logger.error('Error removing vehicle:', error);
        throw error;
      }

      dispatch({ type: 'REMOVE_GARAGE_VEHICLE', payload: vehicleId });
      logger.log('Vehicle removed successfully');
    } catch (error) {
      logger.error('Error removing garage vehicle:', error);
      throw error;
    }
  }, [dispatch]);

  const addMaintenanceRecord = useCallback(async (vehicleId: string, record: MaintenanceRecord): Promise<void> => {
    logger.log('Adding maintenance record for vehicle:', vehicleId);
    
    try {
      // Column names must match the table, which uses service_type /
      // service_date / notes. The previous shape sent `type`, `description`
      // and `date`, none of which exist, so PostgREST rejected every insert
      // and no maintenance record added from the website was ever saved.
      const { error } = await supabase
        .from('maintenance_records')
        .insert({
          vehicle_id: vehicleId,
          service_type: record.type,
          notes: record.description,
          service_date: record.date,
          mileage: record.mileage,
          cost: record.cost
        });

      if (error) {
        logger.error('Error adding maintenance record:', error);
        throw error;
      }

      logger.log('Maintenance record added successfully');
    } catch (error) {
      logger.error('Error adding maintenance record:', error);
      throw error;
    }
  }, []);

  const addFuelRecord = useCallback(async (vehicleId: string, record: FuelRecord): Promise<void> => {
    logger.log('Adding fuel record for vehicle:', vehicleId);
    
    try {
      // `cost` does not exist on fuel_records — the money column is
      // total_cost — and `mileage` is NOT NULL, so an insert without it fails
      // even once the name is right. Both were silently failing before.
      //
      // Mileage is taken from the record rather than defaulted to 0: a zero
      // would be accepted and then silently wreck economy-between-fills and
      // the health score, which is worse than the rejection it replaced.
      if (record.mileage == null || Number.isNaN(Number(record.mileage))) {
        throw new Error('A fuel entry needs the odometer reading.');
      }

      const { error } = await supabase
        .from('fuel_records')
        .insert({
          vehicle_id: vehicleId,
          date: record.date,
          total_cost: record.cost,
          mileage: Number(record.mileage)
        });

      if (error) {
        logger.error('Error adding fuel record:', error);
        throw error;
      }

      logger.log('Fuel record added successfully');
    } catch (error) {
      logger.error('Error adding fuel record:', error);
      throw error;
    }
  }, []);

  const addToFavorites = useCallback(async (vehicle: Vehicle): Promise<FavoriteVehicle | null> => {
    logger.log('Adding vehicle to favorites:', vehicle.id);
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        logger.error('Error getting user for favorites:', userError);
        throw new Error('User not authenticated');
      }

      const { data: insertedFavorite, error: insertError } = await supabase
        .from('favorite_vehicles')
        .insert({
          user_id: user.id,
          vehicle_id: vehicle.id,
          vehicle_data: JSON.stringify(vehicle)
        })
        .select()
        .single();

      if (insertError) {
        logger.error('Error inserting favorite:', insertError);
        throw insertError;
      }

      const favoriteVehicle: FavoriteVehicle = {
        id: insertedFavorite.id,
        vehicleId: insertedFavorite.vehicle_id,
        userId: insertedFavorite.user_id,
        vehicle: JSON.parse(insertedFavorite.vehicle_data),
        addedAt: insertedFavorite.created_at
      };

      dispatch({ type: 'ADD_FAVORITE_VEHICLE', payload: favoriteVehicle });
      logger.log('Vehicle added to favorites successfully');
      return favoriteVehicle;
    } catch (error) {
      logger.error('Error adding vehicle to favorites:', error);
      return null;
    }
  }, [dispatch]);

  const removeFromFavorites = useCallback(async (favoriteId: string): Promise<void> => {
    logger.log('Removing vehicle from favorites:', favoriteId);
    
    try {
      const { error } = await supabase
        .from('favorite_vehicles')
        .delete()
        .eq('id', favoriteId);

      if (error) {
        logger.error('Error removing favorite:', error);
        throw error;
      }

      dispatch({ type: 'REMOVE_FAVORITE_VEHICLE', payload: favoriteId });
      logger.log('Vehicle removed from favorites successfully');
    } catch (error) {
      logger.error('Error removing vehicle from favorites:', error);
      throw error;
    }
  }, [dispatch]);

  const isVehicleFavorited = useCallback((vehicleId: string): boolean => {
    return state.favoriteVehicles.some(fav => fav.vehicleId === vehicleId);
  }, [state.favoriteVehicles]);

  return (
    <AppContext.Provider value={{
      state,
      dispatch,
      checkAndHandleLimits,
      assessVehicle,
      incrementAssessmentUsed,
      incrementCarLensUsed,
      incrementAskCarsjudgeUsed,
      setShowLimitModal,
      resetUsageCounts,
      addToGarage,
      updateGarageVehicle,
      removeFromGarage,
      addMaintenanceRecord,
      addFuelRecord,
      addToFavorites,
      removeFromFavorites,
      isVehicleFavorited
    }}>
      {children}
    </AppContext.Provider>
  );
}

// Hook to use the context
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}